/* eslint-disable camelcase */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Input, Row, Select } from 'antd';

import { useDispatch, useSelector } from 'react-redux';

import { numberWithSpaces } from '../../helpers/formatInteger';
import dayjs from 'dayjs';
import StorageService from '../../services/StorageService';
import API from '../../utils/API';
import { API_URLS } from '../../constants/api_urls';

import fadeDark from './img/fade-composite.png';
import fadeLight from './img/fade-composite-light.png';
import glass from './img/glass-composite.svg';
import { ReactComponent as Delete } from '../../assets/delete-black-icon.svg';

import { getRecommendationMaps } from '../../data/store/instruments/instrumentsActions';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import './style.scss';
import ModalDeletePortfolio from '../../components/ModalDeletePortfolio/ModalDeletePortfolio';

const PortfolioComposeEditInf = ({
  portfolioId,
  form,
  userId,
  setNeedSaveInfo,
  theme,
  totalValue,
}) => {
  const { t } = useTranslation('');
  const dispatch = useDispatch();

  const [modalDeleteVisible, setModalDeleteVisible] = useState(false);
  const [portfolioTypeList, setPortfolioTypeList] = useState(null);

  const advisor = useSelector((state) => state.userReducer.advisor.id);
  const { recommendation } = useSelector((state) => state.instrumentsReducer);

  const {
    // isLoading,
    portfolioById,
    portfolioById: {
      name,
      portfolio_type,
      created_by = advisor ?? null,
      created_at,
    },
  } = useSelector((state) => state.portfolioReducer);
  const [creator, setCreator] = useState();

  useEffect(() => {
    dispatch(getRecommendationMaps());
  }, []);

  useEffect(() => {
    const list = recommendation?.filter((i) => i?.is_complete);
    setPortfolioTypeList(list ?? []);
  }, [recommendation]);

  useEffect(() => {
    if (portfolioId && portfolioId !== 'create') {
      form.setFieldValue('name', name);
      form.setFieldValue('portfolio_type', portfolio_type);
    } else {
      form.setFieldValue('name', t('MY_TOTAL_WEALTH'));
    }
  }, [portfolioId, form, t, portfolioById]);

  useEffect(() => {
    async function fetchData() {
      try {
        const token = StorageService.getJWTToken();
        const response = await API.get(`${API_URLS.ADVISOR_V3}/${created_by}`, {
          headers: { Authorization: 'Bearer ' + token },
        });
        setCreator(
          `${response?.data?.first_name} ${response?.data?.last_name}`,
        );
      } catch (error) {
        console.log('Error', error?.response);
      }
    }
    if (created_by) {
      fetchData();
    }
  }, [created_by]);

  const handleChangeType = (value) => {
    form.setFieldValue('portfolio_type', value);
  };

  const handleChange = (_, allValues) => {
    setNeedSaveInfo(hasChanges(allValues));
  };

  function hasChanges(allValues) {
    const { name, portfolio_type } = allValues;
    if (
      name === portfolioById.name &&
      portfolio_type === portfolioById.portfolio_type
    ) {
      return false;
    } else {
      return true;
    }
  }

  const handleDeleteModal = () => {
    setModalDeleteVisible(true);
  };

  const backgroundSelected = useMemo(() => {
    const color = theme === 'dark-theme' ? '#2D2624' : '#F6F4F3';
    const fade = theme === 'dark-theme' ? fadeDark : fadeLight;

    return `url(${fade}), no-repeat url(${glass}), ${color}`;
  }, [theme, portfolioId]);

  return (
    <>
      <div className="portfolio-info-wrapper">
        <Row justify={'space-between'}>
          <Col span={12}>
            <h3 className="portfolio-info-title">
              {t('PORTFOLIO_INFORMATION')}
            </h3>
          </Col>
          <Col
            span={12}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
            }}
          >
            <Button
              type="text"
              className="round-button"
              icon={<Delete />}
              onClick={handleDeleteModal}
            />
          </Col>
        </Row>
        <div
          className="portfolioinfo-card"
          style={{ background: backgroundSelected }}
        >
          <div>
            <p className="portfolio-card-name">
              {portfolioId && portfolioId !== 'create'
                ? name
                : t('MY_TOTAL_WEALTH')}
            </p>
          </div>
          <div>
            <p className="portfolio-card-value">{`${numberWithSpaces(Math.round(totalValue ?? 0))} kr`}</p>
            <Row
              justify={'center'}
              className="portfolio-card-text"
              style={{ height: '24px' }}
            >
              {/* <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('RETURN')}
                  value={portfolioById?.return}
                />
              </Col>
              <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('VOLATILITY')}
                  value={portfolioById?.vol}
                />
              </Col>
              <Col className="">
                <ColorDataItem
                  isSelected={true}
                  text={t('CVaR')}
                  value={portfolioById?.cvar}
                />
              </Col> */}
            </Row>
          </div>
        </div>
        <div className="portfolio-creator-info-wrapper">
          <div className="portfolio-creator-info-left">
            <h3 className="details-create">{t('CREATED_ON')}</h3>
            <p className="details-date">
              {portfolioId !== 'create'
                ? dayjs(created_at).format('DD MMM YYYY').toUpperCase()
                : name}
            </p>
            <p className="details-createdBy">
              {t('BY')}
              {` ${creator ?? '...'}`}
            </p>
          </div>
        </div>
        <Form
          id="create-portfolio"
          form={form}
          validateTrigger={['onSubmit', 'onBlur']}
          layout="vertical"
          autoComplete="off"
          onValuesChange={handleChange}
          style={{ paddingTop: '28px' }}
        >
          <Form.Item
            label={t('NAME')}
            name="name"
            required={false}
            rules={[
              {
                required: true,
                message: t('PLEASE_ENTER_A_NAME'),
              },
            ]}
          >
            <Input className="custom-input full-width" />
          </Form.Item>
          <Form.Item
            label={t('PORTFOLIO_TYPE')}
            name="portfolio_type"
            required={false}
            rules={[
              {
                required: true,
                message: t('CHOOSE_PORTFOLIO_TYPE'),
              },
            ]}
          >
            <CustomSelect className="custom-select" onChange={handleChangeType}>
              {portfolioTypeList?.map((el) => (
                <Select.Option key={el.id} value={el.id}>
                  {el.name}
                </Select.Option>
              ))}
            </CustomSelect>
          </Form.Item>
        </Form>
      </div>

      <ModalDeletePortfolio
        modalDeleteVisible={modalDeleteVisible}
        setModalDeleteVisible={setModalDeleteVisible}
        portfolioId={portfolioById?.id}
        userId={userId}
        name={name}
      />
    </>
  );
};

export default PortfolioComposeEditInf;

PortfolioComposeEditInf.propTypes = {
  form: PropTypes.object,
  onSubmit: PropTypes.func,
  userId: PropTypes.string,
  portfolioId: PropTypes.string,
  setNeedSaveInfo: PropTypes.func,
  saveAlert: PropTypes.object,
  setSaveAlert: PropTypes.func,
  theme: PropTypes.string,
  totalValue: PropTypes.number,
};
